/**
 * Ruta semipersonalizada: el usuario pone ORIGEN y DESTINO (el origen puede ser el GPS) y una lista de paradas.
 * Cada parada puede llevar categorías (el filtro de actividades) y «a partir de qué km» quiere pararse.
 * Lo que se deja en blanco se resuelve al azar. Lógica pura + red inyectable (para poder probarla sin internet).
 *
 *  1. Se calcula la ruta base origen → destino (OSRM) y sus km totales.
 *  2. Para cada parada se buscan sitios del tipo pedido a lo largo del tramo que empieza en su km, cerca del camino.
 *  3. Se elige el mejor (el más cercano al km pedido, con poco desvío, de la categoría marcada) y las paradas se ordenan por km.
 *  4. Se vuelve a calcular la ruta pasando por las paradas: km, tiempo y enlace de Google Maps.
 */
import { categoriesOfPlace, filterPlacesByCategories, type PlaceCategoryId } from "./categories";
import { dwellFallbackMinutes } from "./dwellAi";
import { fetchNearbyPlaces, interestScore, placeDistanceMeters, type TravelMode } from "./places";
import type { NearbyPlace } from "./types";

export type LatLon = { latitude: number; longitude: number };

export type SemiStop = {
  id: string;
  /** Vacío = cualquier cosa interesante (aleatorio). */
  categories: PlaceCategoryId[];
  /** Km de la ruta a partir de los que se busca la parada (1 … km totales). null = repartido solo. */
  fromKm: number | null;
};

export type RouteGeometry = {
  points: LatLon[];
  /** km acumulados en cada punto (misma longitud que points). */
  cumKm: number[];
  distanceKm: number;
  durationMin: number;
  /** true si no se pudo consultar el servicio de rutas y es una línea recta corregida. */
  approximate: boolean;
};

export type SemiStopResult = {
  stopId: string;
  place: NearbyPlace | null;
  /** Km pedidos (o repartidos automáticamente). */
  requestedKm: number;
  /** Km de la ruta base donde cae la parada. */
  atKm: number | null;
  detourMeters: number | null;
  note: string;
};

export type SemiRoutePlan = {
  base: RouteGeometry;
  final: RouteGeometry;
  stops: SemiStopResult[];
  totalKm: number;
  travelMinutes: number;
  dwellMinutes: number;
  totalMinutes: number;
  unmatched: string[];
};

export type SemiRouteDeps = {
  fetchPlaces?: (center: LatLon, radiusMeters: number) => Promise<NearbyPlace[]>;
  fetchRoute?: (points: LatLon[], mode: TravelMode) => Promise<RouteGeometry>;
  rng?: () => number;
};

/** Km/h para estimar el tiempo cuando el servicio de rutas solo conoce coches. */
const SPEED_KMH: Record<TravelMode, number> = { walk: 4.5, run: 9, bike: 15, car: 60 };
/** Cuánto se puede apartar una parada del camino (m). */
export const CORRIDOR_M: Record<TravelMode, number> = { walk: 600, run: 700, bike: 1500, car: 3000 };
/** Factor de rodeo de una línea recta para aproximar una carretera real. */
const DETOUR_FACTOR = 1.25;

/** Sitios que no son una «parada» sensata si el usuario no ha elegido categoría. */
const UTILITY_KINDS = new Set([
  "pharmacy", "post", "laundry", "bank", "atm", "fuel", "parking", "bus_station", "railway", "airport", "hospital", "school", "university",
  "car_repair", "car_wash", "hairdresser", "beauty", "tattoo", "optician", "wheelchair", "lugar", "coworking", "conference", "marina",
]);

export function haversineMeters(a: LatLon, b: LatLon): number {
  return placeDistanceMeters(a, b);
}

export function buildGeometry(points: LatLon[], durationMin: number | null, mode: TravelMode, approximate: boolean): RouteGeometry {
  const cumKm: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    cumKm.push(cumKm[i - 1] + haversineMeters(points[i - 1], points[i]) / 1000);
  }
  const distanceKm = cumKm[cumKm.length - 1] ?? 0;
  const byMode = (distanceKm / SPEED_KMH[mode]) * 60;
  return { points, cumKm, distanceKm, durationMin: Math.round(durationMin ?? byMode), approximate };
}

/** Ruta por carretera con OSRM; si falla, línea recta con rodeo. */
export async function fetchRouteGeometry(points: LatLon[], mode: TravelMode): Promise<RouteGeometry> {
  const straight = (): RouteGeometry => {
    const g = buildGeometry(points, null, mode, true);
    const scaled = g.cumKm.map((k) => k * DETOUR_FACTOR);
    const distanceKm = scaled[scaled.length - 1] ?? 0;
    return { ...g, cumKm: scaled, distanceKm, durationMin: Math.round((distanceKm / SPEED_KMH[mode]) * 60) };
  };
  if (points.length < 2) return straight();
  const coords = points.map((p) => `${p.longitude},${p.latitude}`).join(";");
  const profile = mode === "car" ? "driving" : mode === "bike" ? "cycling" : "walking";
  const url = `https://router.project-osrm.org/route/v1/${profile}/${coords}?overview=full&geometries=geojson&continue_straight=false`;
  try {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 12000) : null;
    const response = await fetch(url, { headers: { Accept: "application/json" }, signal: controller?.signal });
    if (timer) clearTimeout(timer);
    if (!response.ok) return straight();
    const data = (await response.json()) as { routes?: Array<{ duration?: number; geometry?: { coordinates?: Array<[number, number]> } }> };
    const route = data.routes?.[0];
    const line = route?.geometry?.coordinates;
    if (!line || line.length < 2) return straight();
    const pts = line.map(([longitude, latitude]) => ({ latitude, longitude }));
    // El servidor público solo conoce coches: a pie, en bici o corriendo el tiempo se calcula con la distancia.
    return buildGeometry(pts, mode === "car" && route?.duration != null ? route.duration / 60 : null, mode, false);
  } catch {
    return straight();
  }
}

/** Punto de la ruta situado a `km` desde el origen. */
export function pointAtKm(geom: RouteGeometry, km: number): LatLon {
  const { points, cumKm } = geom;
  if (!points.length) return { latitude: 0, longitude: 0 };
  if (km <= 0) return points[0];
  if (km >= cumKm[cumKm.length - 1]) return points[points.length - 1];
  let lo = 0;
  let hi = cumKm.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cumKm[mid] <= km) lo = mid;
    else hi = mid;
  }
  const span = cumKm[hi] - cumKm[lo] || 1;
  const t = (km - cumKm[lo]) / span;
  return {
    latitude: points[lo].latitude + (points[hi].latitude - points[lo].latitude) * t,
    longitude: points[lo].longitude + (points[hi].longitude - points[lo].longitude) * t,
  };
}

/** Punto más cercano de la ruta a un sitio: km del camino en que cae y distancia (m) que hay que desviarse. */
export function projectOnRoute(geom: RouteGeometry, place: LatLon): { km: number; distanceMeters: number } {
  const { points, cumKm } = geom;
  if (points.length < 2) return { km: 0, distanceMeters: points[0] ? haversineMeters(points[0], place) : 0 };
  const cosLat = Math.cos((place.latitude * Math.PI) / 180);
  const mPerDegLat = 111320;
  const mPerDegLon = 111320 * cosLat;
  let best = { km: 0, distanceMeters: Infinity };
  for (let i = 0; i < points.length - 1; i++) {
    const ax = (points[i].longitude - place.longitude) * mPerDegLon;
    const ay = (points[i].latitude - place.latitude) * mPerDegLat;
    const bx = (points[i + 1].longitude - place.longitude) * mPerDegLon;
    const by = (points[i + 1].latitude - place.latitude) * mPerDegLat;
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2));
    const px = ax + dx * t;
    const py = ay + dy * t;
    const dist = Math.sqrt(px * px + py * py);
    if (dist < best.distanceMeters) {
      best = { km: cumKm[i] + (cumKm[i + 1] - cumKm[i]) * t, distanceMeters: dist };
    }
  }
  return best;
}

/** Km por defecto de las paradas sin km: repartidas a partes iguales (1 parada = mitad; 2 = tercios…). */
export function evenKm(index: number, count: number, totalKm: number): number {
  return Math.round((totalKm * (index + 1)) / (count + 1) * 10) / 10;
}

/** Km pedidos acotados a [1, total] (si la ruta mide menos de 1 km, a [10 %, total]). */
export function clampKm(km: number, totalKm: number): number {
  const min = totalKm >= 1 ? 1 : Math.max(0.05, totalKm * 0.1);
  return Math.max(min, Math.min(totalKm, Number.isFinite(km) ? km : min));
}

function searchRadiusFor(mode: TravelMode): number {
  return Math.max(1500, Math.round(CORRIDOR_M[mode] * 1.6));
}

const defaultFetchPlaces = (center: LatLon, radiusMeters: number) =>
  fetchNearbyPlaces({ latitude: center.latitude, longitude: center.longitude, radius: radiusMeters });

type Candidate = { place: NearbyPlace; km: number; detour: number; score: number };

/** Elige la mejor parada para una búsqueda a partir de `fromKm`. Devuelve null si no hay nada razonable. */
export async function findStopCandidate(input: {
  base: RouteGeometry;
  stop: SemiStop;
  fromKm: number;
  mode: TravelMode;
  minKm: number;
  used: Set<string>;
  deps: Required<Pick<SemiRouteDeps, "fetchPlaces" | "rng">>;
}): Promise<Candidate | null> {
  const { base, stop, fromKm, mode, minKm, used, deps } = input;
  const total = base.distanceKm;
  const random = stop.categories.length === 0;
  // Se mira desde su km y se avanza por el camino (hasta 5 puntos de búsqueda) si ahí no hay nada.
  const step = Math.max(2, total * 0.08);
  const anchors: number[] = [];
  for (let km = fromKm; km <= total - 0.3 && anchors.length < 5; km += step) anchors.push(km);
  if (!anchors.length) anchors.push(Math.min(fromKm, total));
  const seen = new Map<string, NearbyPlace>();

  for (const km of anchors) {
    const center = pointAtKm(base, km);
    let fetched: NearbyPlace[] = [];
    try {
      fetched = await deps.fetchPlaces(center, searchRadiusFor(mode));
    } catch {
      fetched = [];
    }
    for (const p of fetched) if (p.name && !seen.has(p.id)) seen.set(p.id, p);

    let pool = [...seen.values()].filter((p) => !used.has(p.id));
    pool = random
      ? pool.filter((p) => !UTILITY_KINDS.has(p.kind) && interestScore(p) >= 5)
      : filterPlacesByCategories(pool, stop.categories);

    // Primero cerca del camino; si no hay nada, se admite apartarse más (×2, ×3,5).
    for (const factor of [1, 2, 3.5]) {
      const limit = CORRIDOR_M[mode] * factor;
      const found: Candidate[] = [];
      for (const place of pool) {
        const proj = projectOnRoute(base, place);
        if (proj.distanceMeters > limit) continue;
        if (proj.km < Math.max(fromKm - 0.3, minKm)) continue;
        if (proj.km > total - 0.2) continue;
        const detourKm = proj.distanceMeters / 1000;
        const score =
          Math.min(12, interestScore(place)) +
          (place.rating != null ? place.rating * 0.4 : 0) -
          detourKm * (mode === "car" ? 1.2 : 3) -
          Math.max(0, proj.km - fromKm) * 0.15 +
          (random ? deps.rng() * 3 : 0);
        found.push({ place, km: proj.km, detour: proj.distanceMeters, score });
      }
      if (found.length) {
        found.sort((a, b) => b.score - a.score);
        return found[0];
      }
    }
  }
  return null;
}

export async function planSemiRoute(
  input: { origin: LatLon; destination: LatLon; mode: TravelMode; stops: SemiStop[] },
  overrides: SemiRouteDeps = {},
): Promise<SemiRoutePlan> {
  const deps = {
    fetchPlaces: overrides.fetchPlaces ?? defaultFetchPlaces,
    fetchRoute: overrides.fetchRoute ?? fetchRouteGeometry,
    rng: overrides.rng ?? Math.random,
  };
  const { origin, destination, mode } = input;
  const base = await deps.fetchRoute([origin, destination], mode);
  const total = base.distanceKm;
  const n = input.stops.length;

  // Km pedidos de cada parada (los vacíos se reparten solos) y orden de búsqueda por km.
  const requested = input.stops.map((s, i) => clampKm(s.fromKm ?? evenKm(i, n, total), total));
  const order = input.stops.map((_, i) => i).sort((a, b) => requested[a] - requested[b] || a - b);

  const used = new Set<string>();
  const results = new Map<string, SemiStopResult>();
  let minKm = 0;
  for (const i of order) {
    const stop = input.stops[i];
    const fromKm = requested[i];
    const found = await findStopCandidate({ base, stop, fromKm, mode, minKm, used, deps });
    if (!found) {
      results.set(stop.id, {
        stopId: stop.id,
        place: null,
        requestedKm: fromKm,
        atKm: null,
        detourMeters: null,
        note: stop.categories.length
          ? "No he encontrado ningún sitio de esas categorías cerca del camino a partir de ese km. Prueba otro km u otras categorías."
          : "No he encontrado nada interesante cerca del camino a partir de ese km.",
      });
      continue;
    }
    used.add(found.place.id);
    minKm = Math.max(minKm, found.km + 0.4);
    results.set(stop.id, {
      stopId: stop.id,
      place: found.place,
      requestedKm: fromKm,
      atKm: Math.round(found.km * 10) / 10,
      detourMeters: Math.round(found.detour),
      note: "",
    });
  }

  const stopResults = input.stops.map((s) => results.get(s.id)!);
  const chosen = stopResults.filter((r) => r.place).sort((a, b) => (a.atKm ?? 0) - (b.atKm ?? 0));
  const waypoints = chosen.map((r) => ({ latitude: r.place!.latitude, longitude: r.place!.longitude }));
  const final = waypoints.length ? await deps.fetchRoute([origin, ...waypoints, destination], mode) : base;
  const dwellMinutes = chosen.reduce((sum, r) => sum + dwellFallbackMinutes(r.place!.kind), 0);
  return {
    base,
    final,
    stops: stopResults,
    totalKm: Math.round(final.distanceKm * 10) / 10,
    travelMinutes: final.durationMin,
    dwellMinutes,
    totalMinutes: final.durationMin + dwellMinutes,
    unmatched: stopResults.filter((r) => !r.place).map((r) => r.stopId),
  };
}

/** Categorías de un sitio elegido, para enseñarlas en la lista. */
export function stopCategories(place: NearbyPlace): PlaceCategoryId[] {
  return categoriesOfPlace(place);
}

/** Enlace de Google Maps origen → destino pasando por las paradas (Google admite unas 9 paradas intermedias). */
export function googleMapsRouteVia(origin: LatLon, destination: LatLon, stops: LatLon[], mode: TravelMode): string {
  const travel = mode === "car" ? "driving" : mode === "bike" ? "bicycling" : "walking";
  const o = `${origin.latitude},${origin.longitude}`;
  const d = `${destination.latitude},${destination.longitude}`;
  const mids = stops.slice(0, 9).map((s) => `${s.latitude},${s.longitude}`).join("|");
  const wp = mids ? `&waypoints=${encodeURIComponent(mids)}` : "";
  return `https://www.google.com/maps/dir/?api=1&origin=${o}&destination=${d}${wp}&travelmode=${travel}`;
}
